"use client";

import { useEffect, useId, useRef, useState } from "react";
import { usePathname } from "next/navigation";
import { autoUpdate, FloatingPortal, offset, shift, useFloating } from "@floating-ui/react";
import PageStructureThumbnail from "@/app/flow-test/PageStructureThumbnail";
import { workspacePageId, WORKSPACE_NAVIGATION_START, WORKSPACE_PAGE_CREATION_START, type WorkspaceNavigationLabel } from "@/lib/client/workspace-navigation-shell";
import { WORKSPACE_PAGES_UPDATED } from "@/lib/client/workspace-page-creation";
import { WORKSPACE_PREVIEW_CACHE_LIMIT, WORKSPACE_PREVIEW_CACHE_MS, WORKSPACE_PREVIEW_DELAY_MS, WORKSPACE_PAGE_PREVIEW_CLOSED, type WorkspacePagePreview } from "@/lib/workspace-page-preview";

type Target = { anchor: HTMLAnchorElement; label: WorkspaceNavigationLabel };
type Result = { pageId: string; page: WorkspacePagePreview };

/** One delegated hover listener, not a component/editor per navigation row.
 * Only account-known ordinary page links qualify. A delayed, abortable GET
 * retrieves saved structure/cover; no route prefetch, activation or block startup.
 * The parent keys this component by user/account, discarding the small cache. */
export default function WorkspacePageHoverPreview({ identity, getLabel }: {
  identity: string;
  getLabel: (id: string) => WorkspaceNavigationLabel | undefined;
}) {
  const pathname = usePathname();
  const tooltipId = useId();
  const [target, setTarget] = useState<Target | null>(null);
  const [result, setResult] = useState<Result | null>(null);
  const current = result?.pageId === target?.label.id ? result?.page : null;
  const cache = useRef(new Map<string, { page: WorkspacePagePreview; expires: number; revision?: string }>());
  const cacheEpoch = useRef(0);
  const { refs, floatingStyles, isPositioned } = useFloating({
    open: !!target && !!current, placement: "right-start", strategy: "fixed", transform: false,
    // This sidebar is always on the left. Shift the card into a narrow viewport
    // instead of flipping below the row and covering the next page links.
    middleware: [offset(10), shift({ padding: 12, crossAxis: true })], whileElementsMounted: autoUpdate,
  });
  const { setReference, setFloating, floating } = refs;

  useEffect(() => {
    let candidate: Target | null = null;
    let blocked: HTMLAnchorElement | null = null;
    let visible = false;
    let opening: ReturnType<typeof setTimeout> | undefined;
    let closing: ReturnType<typeof setTimeout> | undefined;
    const clearOpening = () => { clearTimeout(opening); opening = undefined; };
    const close = () => {
      clearOpening(); clearTimeout(closing);
      const sidebar = candidate?.anchor.closest<HTMLElement>("[data-flow-sidebar]");
      const wasVisible = visible;
      if (sidebar) delete sidebar.dataset.pagePreviewOpen;
      candidate = null; visible = false; setTarget(null); setResult(null);
      if (wasVisible) window.dispatchEvent(new Event(WORKSPACE_PAGE_PREVIEW_CLOSED));
    };
    const anchorOf = (node: EventTarget | null) => {
      const anchor = node instanceof Element ? node.closest<HTMLAnchorElement>("a[href]") : null;
      if (!anchor || !anchor.closest("[data-flow-sidebar]") || anchor.closest('[contenteditable="true"], [inert]')) return null;
      const url = new URL(anchor.href, window.location.href);
      if (url.origin !== location.origin || url.search || url.hash) return null;
      const id = workspacePageId(url.pathname);
      if (!id) return null;
      const label = getLabel(id);
      return label ? { anchor, label } : null;
    };
    const inside = (node: EventTarget | null) => node instanceof Node && !!floating.current?.contains(node);
    const start = (next: Target | null) => {
      clearTimeout(closing);
      if (!next || next.anchor === blocked) return;
      if (candidate?.anchor === next.anchor) return;
      close(); candidate = next;
      opening = setTimeout(() => {
        if (!next.anchor.isConnected || !next.anchor.getClientRects().length
          || (!next.anchor.matches(":hover") && !next.anchor.matches(":focus-visible"))) { close(); return; }
        visible = true;
        const sidebar = next.anchor.closest<HTMLElement>("[data-flow-sidebar]");
        if (sidebar) sidebar.dataset.pagePreviewOpen = "true";
        setReference(next.anchor); setTarget(next);
      }, WORKSPACE_PREVIEW_DELAY_MS);
    };
    const over = (event: PointerEvent) => {
      if (event.pointerType === "touch") return;
      if (inside(event.target)) { clearTimeout(closing); return; }
      const next = anchorOf(event.target);
      if (next?.anchor === blocked && anchorOf(event.relatedTarget)?.anchor !== blocked && !inside(event.relatedTarget)) blocked = null;
      if (next?.anchor !== anchorOf(event.relatedTarget)?.anchor) start(next);
    };
    const out = (event: PointerEvent) => {
      if (event.pointerType === "touch") return;
      const next = anchorOf(event.relatedTarget);
      const previous = anchorOf(event.target);
      if (blocked === previous?.anchor && next?.anchor !== blocked) blocked = null;
      // Removing the old account's floating portal may emit a late pointerout.
      // Only leaving this candidate/card can cancel its opening timer.
      if (!candidate || (previous?.anchor !== candidate.anchor && !inside(event.target))) return;
      if ((candidate && next?.anchor === candidate.anchor) || inside(event.relatedTarget)) return;
      if (candidate?.anchor === document.activeElement && candidate.anchor.matches(":focus-visible")) return;
      closing = setTimeout(() => {
        if (candidate?.anchor.matches(":hover") || floating.current?.matches(":hover")) return;
        close();
      }, 160);
    };
    const focus = (event: FocusEvent) => {
      const next = anchorOf(event.target);
      if (next?.anchor.matches(":focus-visible")) start(next);
    };
    const blur = (event: FocusEvent) => {
      if (event.target !== candidate?.anchor || inside(event.relatedTarget)) return;
      blocked = null; close();
    };
    const escape = (event: KeyboardEvent) => {
      if (event.key !== "Escape" || !candidate) return;
      const wasVisible = !!floating.current;
      blocked = candidate.anchor; close();
      if (wasVisible) event.stopPropagation();
    };
    const scroll = (event: Event) => { if (!inside(event.target)) close(); };
    const changed = () => { cacheEpoch.current++; cache.current.clear(); close(); };
    document.addEventListener("pointerover", over);
    document.addEventListener("pointerout", out);
    document.addEventListener("focusin", focus);
    document.addEventListener("focusout", blur);
    document.addEventListener("pointerdown", close, true);
    document.addEventListener("keydown", escape, true);
    document.addEventListener("scroll", scroll, true);
    window.addEventListener("blur", close);
    window.addEventListener(WORKSPACE_NAVIGATION_START, close);
    window.addEventListener(WORKSPACE_PAGE_CREATION_START, close);
    window.addEventListener(WORKSPACE_PAGES_UPDATED, changed);
    return () => {
      close();
      document.removeEventListener("pointerover", over);
      document.removeEventListener("pointerout", out);
      document.removeEventListener("focusin", focus);
      document.removeEventListener("focusout", blur);
      document.removeEventListener("pointerdown", close, true);
      document.removeEventListener("keydown", escape, true);
      document.removeEventListener("scroll", scroll, true);
      window.removeEventListener("blur", close);
      window.removeEventListener(WORKSPACE_NAVIGATION_START, close);
      window.removeEventListener(WORKSPACE_PAGE_CREATION_START, close);
      window.removeEventListener(WORKSPACE_PAGES_UPDATED, changed);
    };
  }, [pathname, getLabel, setReference, floating]);

  useEffect(() => {
    if (!target) return;
    const { label } = target;
    const controller = new AbortController();
    let alive = true;
    const epoch = cacheEpoch.current;
    const timer = setTimeout(() => controller.abort(), 10_000);
    const cached = cache.current.get(label.id);
    if (cached && cached.expires > Date.now() && cached.revision === label.updated_at) {
      setResult({ pageId: label.id, page: cached.page });
    } else {
      setResult(null);
      void fetch(`/api/workspace-pages/${encodeURIComponent(label.id)}/preview`, {
        credentials: "same-origin", cache: "no-store", signal: controller.signal,
      }).then(async response => {
        if (!response.ok) throw new Error("Preview unavailable");
        const page: WorkspacePagePreview = await response.json();
        if (page.identity !== identity || page.pageId !== label.id) throw new Error("Preview scope changed");
        if (!alive || epoch !== cacheEpoch.current) return;
        if (cache.current.size >= WORKSPACE_PREVIEW_CACHE_LIMIT) cache.current.delete(cache.current.keys().next().value!);
        cache.current.set(label.id, { page, expires: Date.now() + WORKSPACE_PREVIEW_CACHE_MS, revision: label.updated_at });
        setResult({ pageId: label.id, page });
      }).catch(() => { if (alive) setResult(null); });
    }
    return () => {
      alive = false; clearTimeout(timer); controller.abort();
    };
  }, [target, identity]);

  useEffect(() => {
    if (!target || !current) return;
    const { anchor } = target;
    anchor.setAttribute("aria-describedby", [anchor.getAttribute("aria-describedby"), tooltipId].filter(Boolean).join(" "));
    return () => {
      const description = (anchor.getAttribute("aria-describedby") ?? "").split(" ").filter(id => id !== tooltipId).join(" ");
      if (description) anchor.setAttribute("aria-describedby", description); else anchor.removeAttribute("aria-describedby");
    };
  }, [target, current, tooltipId]);

  // Keep pending/failed reads invisible: the card opens once, already complete.
  if (!target || !current) return null;
  return <FloatingPortal><div ref={setFloating} style={{ ...floatingStyles, visibility: isPositioned ? "visible" : "hidden" }} id={tooltipId} role="tooltip" aria-label={current.description.trim() ? undefined : "Схема содержимого страницы"}
    data-workspace-page-hover-preview data-workspace-preview-page-id={target.label.id} className="flow-font z-[180] w-[200px] max-w-[calc(100vw-24px)] max-h-[calc(100dvh-24px)] overflow-auto rounded-xl border border-[#E7E3D9] bg-[#FFFEFC] text-[#37352F] shadow-[0_8px_28px_rgba(55,53,47,0.13)]">
    <PreviewContents key={target.label.id} page={current} />
  </div></FloatingPortal>;
}

function PreviewContents({ page }: { page: WorkspacePagePreview }) {
  return <>
    {page.structure?.length ? <PageStructureThumbnail thumb={page.structure} />
      : <div className="flex h-[132px] items-center border-b border-[#F2F0EA] bg-white px-3.5"><p className="line-clamp-4 break-words text-[10px] leading-[16px] text-[#969085]">Откройте страницу, чтобы увидеть содержимое</p></div>}
    {page.description.trim() && <p className="line-clamp-2 break-words bg-[#FBFAF8] px-3 py-2 text-[11px] leading-[15px] text-[#969085]">{page.description}</p>}
  </>;
}
